import { inject, Injectable } from '@angular/core';
import { shipmentDirectionConditions } from '@core/helpers/shipment-direction-calculation-helper';
import { IServiceAndPackageAvailability } from '@core/models/service-and-package-availability-request';
import { ShipmentDirectionEnum } from '@core/models/shipment-type';
import { TimezoneService } from '@core/services/timezone/timezone.service';
import {
  ClearShipOrQuote,
  SetAvailableLabelTypes,
  SetDefaultLabelType,
  SetEstimatedDeliveryLoading,
  SetEstimatedDeliveryMinShipDate,
  SetEstimatedDeliveryShipDate,
  SetInternationalDocumentOnlyShipment,
  SetLabelType,
  SetPackages,
  SetPackageType,
  SetPreferredCarrier,
  SetServiceAndPackageAvailabilityLoading,
  SetServiceOptions,
  SetShipmentDestination,
  SetShipmentDirectionCalculation,
  SetShipmentEEIShowComponent,
  SetShipmentOrigin,
  SetShipmentServiceOptions,
} from '@core/state/ship-or-quote/ship-or-quote.actions';
import {
  selectEEIRequestDetails,
  selectLabelTypeRequestDetails,
  selectPackageType,
  selectServiceAndPackagesRequestDetails,
  selectServiceAndPackagesRequestDetailsWithShipDate,
  selectShipmentOriginDestination,
} from '@core/state/ship-or-quote/ship-or-quote.selectors';
import { GetCurrentDateTimeByZone } from '@core/utils/current-time-by-zone-offset';
import { GetStandardStringDate } from '@core/utils/get-standard-string-date';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import deepEqual from 'deep-equal';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs';
import { DangerousGoodsEnum } from 'src/app/modules/shipping/features/shipOrQuote/models/dangerousGoodsEnum';
import { ILabelTypeDecision } from 'src/app/modules/shipping/features/shipOrQuote/models/labelTypeDecision';
import { LabelTypeEnum } from 'src/app/modules/shipping/features/shipOrQuote/models/labelTypeEnum';
import { PreferredCarrierEnum } from 'src/app/modules/shipping/features/shipOrQuote/models/preferredCarrierEnum';
import { IEeiCheckDecision } from 'src/app/modules/shipping/features/shipOrQuote/models/shipment/eei-check.interface';
import { IPackageType } from 'src/app/modules/shipping/features/shipOrQuote/models/shipment/packageType';
import { IExpressShipmentService } from 'src/app/modules/shipping/features/shipOrQuote/services/express-shipment-service.interface';
import { ExpressShipmentService } from 'src/app/modules/shipping/features/shipOrQuote/services/express-shipment.service';

@Injectable()
export class ShipOrQuoteEffects {
  actions$ = inject(Actions);
  store = inject(Store<any>);
  timezoneService = inject(TimezoneService);
  expressShipmentService = inject(ExpressShipmentService);
  service: IExpressShipmentService = this.expressShipmentService;
  lastLoadingSetObject: any = {};

  private restartOnClear<T>(effect: () => Observable<T>): Observable<T> {
    return this.actions$.pipe(
      ofType(ClearShipOrQuote),
      startWith(null),
      switchMap(() => effect())
    );
  }

  loadShipmentDirectionCalculation = createEffect(
    () =>
      this.restartOnClear(() =>
        this.actions$.pipe(
          ofType(SetShipmentOrigin, SetShipmentDestination),
          debounceTime(100),
          switchMap(() => {
            return this.store.pipe(select(selectShipmentOriginDestination));
          }),
          filter(
            ({ origin, destination }: any) =>
              Boolean(origin?.value?.shipmentAddress?.id) &&
              Boolean(destination?.value?.shipmentAddress?.id)
          ),
          distinctUntilChanged((prev: any, curr: any) => {
            return (
              prev.origin?.value?.shipmentAddress?.id ===
                curr.origin?.value?.shipmentAddress?.id &&
              prev.destination?.value?.shipmentAddress?.id ===
                curr.destination?.value?.shipmentAddress?.id
            );
          }),
          switchMap(({ origin, destination }: any) => {
            const originAddressId = origin?.value?.shipmentAddress?.id;
            const destinationAddress = destination?.value?.shipmentAddress;
            this.store.dispatch(
              SetShipmentDirectionCalculation({
                directionCalculation: {
                  valid: false,
                  shipmentDirection: ShipmentDirectionEnum.WITHIN_US,
                  deliveryCountryGroup: '',
                  international: false,
                },
              })
            );

            return this.service
              .getShipmentDirectionCalculation({
                shipmentPickupLocation: { addressId: originAddressId },
                shipmentDestinationList: [destinationAddress],
              })
              .pipe(
                map((response) => {
                  if (response.success) {
                    this.store.dispatch(
                      SetShipmentDirectionCalculation({
                        directionCalculation: {
                          deliveryCountryGroup:
                            response.data.deliveryCountryGroup,
                          shipmentDirection: response.data.shipmentType,
                          international:
                            response.data.shipmentType !==
                              ShipmentDirectionEnum.WITHIN_US &&
                            response.data.shipmentType !==
                              ShipmentDirectionEnum.NON_US_DOMESTIC,
                          valid: true,
                        },
                      })
                    );
                  }
                }),
                catchError(() => EMPTY)
              );
          })
        )
      ),
    { dispatch: false }
  );

  // Derives the direction from the country codes alone, for addresses that have
  // no saved id (loadShipmentDirectionCalculation needs one to call the API).
  loadShipmentDirectionCalculationByCountryCode = createEffect(
    () =>
      this.restartOnClear(() =>
        this.actions$.pipe(
          ofType(SetShipmentOrigin, SetShipmentDestination),
          debounceTime(100),
          switchMap(() => {
            return this.store.pipe(select(selectShipmentOriginDestination));
          }),
          map(({ origin, destination }: any) => ({
            originCountryCode: origin?.value?.shipmentAddress?.countryCode,
            destinationCountryCode:
              destination?.value?.shipmentAddress?.countryCode,
          })),
          filter(
            ({ originCountryCode, destinationCountryCode }) =>
              Boolean(originCountryCode) && Boolean(destinationCountryCode)
          ),
          distinctUntilChanged(
            (prev, curr) =>
              prev.originCountryCode === curr.originCountryCode &&
              prev.destinationCountryCode === curr.destinationCountryCode
          ),
          map(({ originCountryCode, destinationCountryCode }) => {
            const shipmentDirection = shipmentDirectionConditions.find(
              (condition) =>
                condition.matches({ originCountryCode, destinationCountryCode })
            )?.type;

            this.store.dispatch(
              SetShipmentDirectionCalculation({
                directionCalculation: {
                  valid: Boolean(shipmentDirection),
                  shipmentDirection:
                    shipmentDirection ?? ShipmentDirectionEnum.WITHIN_US,
                  deliveryCountryGroup: '',
                  international:
                    Boolean(shipmentDirection) &&
                    shipmentDirection !== ShipmentDirectionEnum.WITHIN_US &&
                    shipmentDirection !== ShipmentDirectionEnum.NON_US_DOMESTIC,
                },
              })
            );
          })
        )
      ),
    { dispatch: false }
  );

  loadLabelType = createEffect(
    () =>
      this.restartOnClear(() =>
        this.actions$.pipe(
          ofType(
            SetShipmentOrigin,
            SetShipmentDestination,
            SetPreferredCarrier
          ),
          debounceTime(300),
          switchMap((e) => {
            return this.store.pipe(
              select(selectLabelTypeRequestDetails),
              map(({ origin, destination, preferredCarrier }) => {
                const originZipCode = origin?.value?.shipmentAddress?.zipCode;
                const originCountryCode =
                  origin?.value?.shipmentAddress?.countryCode;
                const destinationCountryCode =
                  destination?.value?.shipmentAddress?.countryCode;

                return {
                  originZipCode,
                  originCountryCode,
                  destinationCountryCode,
                  preferredCarrier,
                };
              })
            );
          }),
          filter(
            ({
              originZipCode,
              originCountryCode,
              destinationCountryCode,
            }: any) =>
              Boolean(originZipCode) &&
              Boolean(originCountryCode) &&
              Boolean(destinationCountryCode)
          ),
          distinctUntilChanged((prev: any, curr: any) => {
            return (
              prev.originZipCode === curr.originZipCode &&
              prev.originCountryCode === curr.originCountryCode &&
              prev.destinationCountryCode === curr.destinationCountryCode &&
              prev.preferredCarrier === curr.preferredCarrier
            );
          }),
          switchMap(
            ({
              originZipCode,
              originCountryCode,
              destinationCountryCode,
              preferredCarrier,
            }: any) => {
              const data: ILabelTypeDecision = {
                originCountryCode,
                originZipCode,
                destinationCountryCode,
                dangerousGoodsList: [DangerousGoodsEnum.NO], // default value
              };

              if (preferredCarrier) {
                data.selectedLabelType = preferredCarrier as LabelTypeEnum;
              }

              return this.service.postExpressShipmentLabelType(data).pipe(
                map((response) => {
                  this.store.dispatch(
                    SetLabelType({ labelType: response.data.actualLabelType })
                  );
                  this.store.dispatch(
                    SetDefaultLabelType({
                      defaultLabelType: response.data.defaultLabelType,
                    })
                  );
                  this.store.dispatch(
                    SetAvailableLabelTypes({
                      availableLabelTypes: response.data.availableLabelTypes,
                    })
                  );
                }),
                catchError(() => EMPTY)
              );
            }
          )
        )
      ),
    { dispatch: false }
  );

  loadServicesPackages = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetLabelType),
        debounceTime(500),
        startWith(null), // trigger an initial run for comparison
        switchMap(() => {
          return this.store.pipe(
            select(selectServiceAndPackagesRequestDetails)
            // filter(
            //   ({ originAddressId, destinationAddressId }: any) =>
            //     Boolean(originAddressId) && Boolean(destinationAddressId)
            // )
          );
        }),
        pairwise(),
        map(([prev, curr]) => {
          if (deepEqual(curr, this.lastLoadingSetObject)) {
            return curr;
          }

          if (deepEqual(prev, curr)) {
            this.store.dispatch(
              SetServiceAndPackageAvailabilityLoading({
                loading: false,
              })
            );
          } else {
            // set loading state to true if we have different data
            this.store.dispatch(
              SetServiceAndPackageAvailabilityLoading({
                loading: true,
              })
            );
          }
          this.lastLoadingSetObject = curr;
          return curr;
        }),
        distinctUntilChanged((prev, curr) => {
          return deepEqual(prev, curr);
        }),
        switchMap(async (data) => {
          const {
            originAddress,
            originAddressId,
            originAddressCountryCode,
            destinationAddress,
            destinationAddressId,
            destinationAddressCountryCode,
            preferredCarrier,
          } = data;

          let requestBody = {
            shipmentPickupLocation: {
              addressId: originAddressId,
              city: originAddress?.city,
              countryCode: originAddressCountryCode,
              stateCode: originAddress?.stateCode,
              streetAddress: originAddress?.streetAddress,
              zipCode: originAddress?.zipCode,
            },
            shipmentDestinationList: [
              { addressId: destinationAddressId, ...destinationAddress },
            ],
            labelTypeDecision: {
              originCountryCode: originAddressCountryCode,
              originZipCode: originAddress?.zipCode,
              destinationCountryCode: destinationAddressCountryCode,
              dangerousGoodsList: [DangerousGoodsEnum.NO], // default value
            } as any,
            returnLabel: false,
          };

          const addressInfo = {
            regionCode: originAddressCountryCode,
            state: originAddress?.stateCode || '',
            zipCode: originAddress?.zipCode || '',
            city: originAddress?.city || '',
          };

          const tzIdentifier =
            await this.timezoneService.getTzIdentifier(addressInfo);

          const minShipDate = GetStandardStringDate(
            GetCurrentDateTimeByZone(tzIdentifier)
          );

          this.store.dispatch(
            SetEstimatedDeliveryMinShipDate({ minShipDate: minShipDate })
          );

          // set UI label if exists
          if (preferredCarrier) {
            requestBody.labelTypeDecision.selectedLabelType =
              preferredCarrier as any;
          }

          return requestBody;
        }),
        filter((requestBody) => Object.keys(requestBody).length > 0),
        switchMap((requestBody: IServiceAndPackageAvailability) => {
          this.store.dispatch(
            SetServiceAndPackageAvailabilityLoading({
              loading: true,
            })
          );
          this.store.dispatch(SetEstimatedDeliveryLoading({ loading: true }));

          return this.service
            .getPackageTypesWithServiceLevels(requestBody)
            .pipe(
              map((response: any) => {
                const newPackages = response?.data || [];

                //reset package type and service options
                this.store.dispatch(SetPackageType({ packageType: null }));
                this.store.dispatch(SetServiceOptions({ serviceOptions: [] }));

                //set new packages
                this.store.dispatch(
                  SetPackages({
                    packages: newPackages,
                  })
                );
              }),
              finalize(() => {
                //set loadings to false
                this.store.dispatch(
                  SetServiceAndPackageAvailabilityLoading({
                    loading: false,
                  })
                );
                this.store.dispatch(
                  SetEstimatedDeliveryLoading({ loading: false })
                );
              }),
              catchError(() => EMPTY)
            );
        })
      ),
    { dispatch: false }
  );

  loadServicesPackagesByShipDate = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetEstimatedDeliveryShipDate),
        switchMap((e) => {
          return this.store.pipe(
            select(selectServiceAndPackagesRequestDetailsWithShipDate),
            filter(
              (data) =>
                data.isOriginValid &&
                data.isDestinationValid &&
                typeof data.shipDate === 'string'
            )
          );
        }),
        debounceTime(500),
        distinctUntilChanged(deepEqual),
        switchMap(async (data) => {
          const {
            originAddress,
            originAddressId,
            originAddressCountryCode,
            destinationAddress,
            destinationAddressId,
            destinationAddressCountryCode,
            preferredCarrier,
            shipDate,
          } = data;

          let requestBody = {
            shipmentPickupLocation: {
              addressId: originAddressId,
              city: originAddress?.city,
              countryCode: originAddressCountryCode,
              stateCode: originAddress?.stateCode,
              streetAddress: originAddress?.streetAddress,
              zipCode: originAddress?.zipCode,
            },
            shipmentDestinationList: [
              { addressId: destinationAddressId, ...destinationAddress },
            ],
            labelTypeDecision: {
              originCountryCode: originAddressCountryCode,
              originZipCode: originAddress?.zipCode,
              destinationCountryCode: destinationAddressCountryCode,
              dangerousGoodsList: [DangerousGoodsEnum.NO], // default value
            } as any,
            returnLabel: false,
            shipDate,
          };
          if (!shipDate) return EMPTY;

          const addressInfo = {
            regionCode: originAddressCountryCode,
            state: originAddress?.stateCode || '',
            zipCode: originAddress?.zipCode || '',
            city: originAddress?.city || '',
          };

          const tzIdentifier =
            await this.timezoneService.getTzIdentifier(addressInfo);

          const minShipDate = GetStandardStringDate(
            GetCurrentDateTimeByZone(tzIdentifier)
          );

          this.store.dispatch(
            SetEstimatedDeliveryMinShipDate({ minShipDate: minShipDate })
          );

          const _shipDate = !!requestBody.shipDate
            ? requestBody.shipDate
            : minShipDate;

          requestBody.shipDate = _shipDate;

          // set UI label if exists
          if (preferredCarrier) {
            requestBody.labelTypeDecision.selectedLabelType =
              preferredCarrier as any;
          }

          return requestBody;
        }),
        switchMap((requestBody: any) => {
          this.store.dispatch(SetEstimatedDeliveryLoading({ loading: true }));

          this.store.dispatch(
            SetShipmentServiceOptions({
              serviceOptions: {},
            })
          );

          return this.service
            .getPackageTypesWithServiceLevels(requestBody)
            .pipe(
              map((response: any) => {
                // in case of an error on given ship date
                if (!response) {
                  this.store.dispatch(
                    SetServiceOptions({
                      serviceOptions: [],
                    })
                  );
                }

                const newPackages = response?.data;

                // set services options by selected package type
                this.store
                  .select(selectPackageType)
                  .subscribe((selectedPackageType) => {
                    if (selectedPackageType) {
                      const selectedPackages = newPackages.filter(
                        (_package: IPackageType) =>
                          _package.packageId === selectedPackageType.packageId
                      );

                      if (selectedPackages.length > 0) {
                        this.store.dispatch(
                          SetServiceOptions({
                            serviceOptions: selectedPackages[0].serviceLevels,
                          })
                        );
                      } else {
                        // if the selected package type is not available on the selected ship date, clear the services list
                        this.store.dispatch(
                          SetServiceOptions({
                            serviceOptions: [],
                          })
                        );
                      }
                    }
                  });
              }),
              finalize(() => {
                //set loadings to false
                this.store.dispatch(
                  SetEstimatedDeliveryLoading({ loading: false })
                );
              }),
              catchError(() => EMPTY)
            );
        })
      ),
    { dispatch: false }
  );

  checkIEEState = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SetInternationalDocumentOnlyShipment),
        switchMap(() => this.store.pipe(select(selectEEIRequestDetails))),
        pairwise(),
        filter(([prev, curr]) => !deepEqual(prev, curr)),
        map(([, curr]) => {
          return curr;
        }),
        filter(
          ({ origin, destination }) =>
            Boolean(origin.value?.shipmentAddress?.countryCode) &&
            Boolean(destination.value.shipmentAddress?.countryCode)
        ), // prevent send invalid requests after clear some address
        switchMap((requestBody) => {
          if (
            (requestBody.preferredCarrier === PreferredCarrierEnum.FEDEX ||
              requestBody.preferredCarrier === PreferredCarrierEnum.UPS) &&
            (requestBody.whatAreYouShipping.documentOnlyShipment === true ||
              requestBody.commodities.commodityList.length > 0)
          ) {
            const body = {
              originCountryCode:
                requestBody.origin.value.shipmentAddress?.countryCode,
              originZipCode: requestBody.origin.value.shipmentAddress?.zipCode,
              destinationCountryCode:
                requestBody.destination.value.shipmentAddress?.countryCode,
              dangerousGoodsList: requestBody.dangerousGoods,
              commodities: [] as any[],
              packages: requestBody.packages,
            };

            if (requestBody.whatAreYouShipping?.documentOnlyShipment == false) {
              body.commodities = requestBody.commodities.commodityList || [];
            }

            return this.service
              .postExpressShipmentCheckForEEI(body as IEeiCheckDecision)
              .pipe(
                map((response) => {
                  this.store.dispatch(
                    SetShipmentEEIShowComponent({
                      show: response.data.shouldContentBeCheckedForEEI,
                    })
                  );
                }),
                catchError(() => EMPTY)
              );
          } else {
            return of(false).pipe(
              map((show) => {
                return this.store.dispatch(
                  SetShipmentEEIShowComponent({
                    show,
                  })
                );
              })
            );
          }
        })
      );
    },
    { dispatch: false }
  );
}
